import {isSketchLineObject} from '../../mesh/guards';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {isPointCoincidentConstraint} from '../solver/constraint/relationDefinition/point/PointCoincidentConstraint';
import {SketchClosureResolver} from './SketchClosureResolver';
import type {SketchClosureTraversalStep} from './SketchClosureTypes';

//------ 草图闭合端点统一 开始------
/**
 * 草图闭合端点统一器。
 *
 * 设计说明：
 * 1. 闭合搜索允许两个独立端点通过坐标容差归并为同一拓扑顶点；
 * 2. 集合正式落地后必须进一步统一端点对象，否则拖动其中一个点会把闭环重新拉开；
 * 3. 直线与曲线相接时优先保留曲线端点，只改绑直线端点，避免为拓扑合并重算曲线定义；
 * 4. 若候选点还连接闭环之外的实体，则不强行合并，防止无意改变第三条草图实体的拓扑。
 * 5. 已由显式重合关系连接的独立点属于参数系统拓扑，不再执行对象合并。
 */
export class SketchClosureEndpointCanonicalizer {
    /**
     * 将闭合遍历链中仅按坐标重合的相邻端点统一为共享点对象。
     * 已存在显式重合关系时保留独立点，由参数系统维持连接。
     *
     * @param traversalSteps 已解析的闭合有向遍历步骤。
     */
    public canonicalize(traversalSteps: readonly SketchClosureTraversalStep[]): void {
        if (traversalSteps.length < 2) {
            return;
        }

        const closedSourceIdSet = new Set(traversalSteps.map((step) => step.source.uuid));
        traversalSteps.forEach((currentStep, currentIndex) => {
            const nextStep = traversalSteps[(currentIndex + 1) % traversalSteps.length];
            if (!nextStep) {
                return;
            }
            this.canonicalizeJunction(currentStep, nextStep, closedSourceIdSet);
        });
    }

    /**
     * 统一两条相邻遍历边的连接端点。
     *
     * @param currentStep 当前遍历边。
     * @param nextStep 下一条遍历边。
     * @param closedSourceIdSet 当前闭环包含的源边 ID。
     */
    private canonicalizeJunction(
        currentStep: SketchClosureTraversalStep,
        nextStep: SketchClosureTraversalStep,
        closedSourceIdSet: ReadonlySet<string>,
    ): void {
        const currentEndPoint = SketchClosureResolver.getTraversalStepEndpoints(currentStep).endPoint;
        const nextStartPoint = SketchClosureResolver.getTraversalStepEndpoints(nextStep).startPoint;
        if (currentEndPoint === nextStartPoint) {
            return;
        }
        if (!this.arePointsCoincident(currentStep.source, currentEndPoint, nextStartPoint)) {
            return;
        }

        /**
         * 显式重合关系本身就是当前参数系统中的连接拓扑，必须保留独立目标点。
         * 转换实体引用依赖该关系实现 SolidWorks 的“可删除重合”行为；若继续合并点对象，
         * 被替换点会离开草图树，而关系仍持有旧目标，最终破坏持久化和历史快照克隆。
         */
        if (this.hasSharedCoincidentRelation(currentEndPoint, nextStartPoint)) {
            return;
        }

        const canonicalPoint = this.resolveCanonicalPoint(
            currentStep,
            currentEndPoint,
            nextStep,
            nextStartPoint,
            closedSourceIdSet,
        );
        if (!canonicalPoint) {
            return;
        }

        if (canonicalPoint === currentEndPoint) {
            this.replaceTraversalEndpoint(nextStep, true, canonicalPoint);
            this.removeDetachedPoint(nextStartPoint, canonicalPoint);
            return;
        }

        this.replaceTraversalEndpoint(currentStep, false, canonicalPoint);
        this.removeDetachedPoint(currentEndPoint, canonicalPoint);
    }

    /**
     * 判断两个独立端点是否由同一条显式重合关系连接。
     *
     * 说明：
     * 1. 关系实例会绑定到每个直接目标的关系容器，因此从任一端点读取即可；
     * 2. 必须同时检查关系目标，避免同一端点上的其它重合关系误阻止当前连接处收口；
     * 3. 这里只识别真实领域关系，不处理尺寸派生只读视图。
     *
     * @param firstPoint 第一个连接端点。
     * @param secondPoint 第二个连接端点。
     * @returns 两个端点由同一条显式重合关系连接时返回 true。
     */
    private hasSharedCoincidentRelation(
        firstPoint: SketchPointObject,
        secondPoint: SketchPointObject,
    ): boolean {
        return firstPoint.constraints.getRelations().some((relation) => {
            if (!isPointCoincidentConstraint(relation)) {
                return false;
            }

            const relationTargets = relation.getTargetsSnapshot();
            return relationTargets.includes(firstPoint) && relationTargets.includes(secondPoint);
        });
    }

    /**
     * 选择连接处最终共享的端点对象。
     *
     * @param currentStep 当前遍历边。
     * @param currentEndPoint 当前遍历终点。
     * @param nextStep 下一条遍历边。
     * @param nextStartPoint 下一条遍历起点。
     * @param closedSourceIdSet 当前闭环包含的源边 ID。
     * @returns 可安全复用的共享点；两点都被闭环外实体使用时返回 null。
     */
    private resolveCanonicalPoint(
        currentStep: SketchClosureTraversalStep,
        currentEndPoint: SketchPointObject,
        nextStep: SketchClosureTraversalStep,
        nextStartPoint: SketchPointObject,
        closedSourceIdSet: ReadonlySet<string>,
    ): SketchPointObject | null {
        const currentPointIsReusable = this.hasOnlyClosedSourceConnections(currentEndPoint, closedSourceIdSet);
        const nextPointIsReusable = this.hasOnlyClosedSourceConnections(nextStartPoint, closedSourceIdSet);

        // 直线与曲线连接时保留曲线端点，确保闭合收口只改变拓扑，不触发曲线重新拟合。
        if (isSketchLineObject(currentStep.source) && !isSketchLineObject(nextStep.source) && nextPointIsReusable) {
            return nextStartPoint;
        }
        if (!isSketchLineObject(currentStep.source) && isSketchLineObject(nextStep.source) && currentPointIsReusable) {
            return currentEndPoint;
        }

        if (currentPointIsReusable) {
            return currentEndPoint;
        }
        if (nextPointIsReusable) {
            return nextStartPoint;
        }
        return null;
    }

    /**
     * 判断端点是否只连接当前闭环内的边。
     *
     * @param point 待判断端点。
     * @param closedSourceIdSet 当前闭环包含的源边 ID。
     * @returns 没有闭环外连接边时返回 true。
     */
    private hasOnlyClosedSourceConnections(
        point: SketchPointObject,
        closedSourceIdSet: ReadonlySet<string>,
    ): boolean {
        return point.getConnectedEdgesSnapshot().every((edge) => closedSourceIdSet.has(edge.uuid));
    }

    /**
     * 判断两个端点是否在草图平面闭合容差内重合。
     *
     * @param source 提供参考面的草图边。
     * @param firstPoint 第一个端点。
     * @param secondPoint 第二个端点。
     * @returns 坐标重合时返回 true。
     */
    private arePointsCoincident(
        source: CollectSource,
        firstPoint: SketchPointObject,
        secondPoint: SketchPointObject,
    ): boolean {
        const firstLocalPoint = source.referencePlane.worldToLocal(firstPoint.getPosition());
        const secondLocalPoint = source.referencePlane.worldToLocal(secondPoint.getPosition());
        const toleranceSquared = SKETCH_GEOMETRY_TOLERANCE_CONFIG.collectionClosurePosition ** 2;
        return firstLocalPoint.distanceToSquared(secondLocalPoint) <= toleranceSquared;
    }

    /**
     * 替换遍历步骤指定方向上的端点引用。
     *
     * @param step 待修改遍历步骤。
     * @param replaceTraversalStart 是否替换遍历起点；false 表示替换遍历终点。
     * @param point 新共享点对象。
     */
    private replaceTraversalEndpoint(
        step: SketchClosureTraversalStep,
        replaceTraversalStart: boolean,
        point: SketchPointObject,
    ): void {
        const replaceSourceStart = replaceTraversalStart ? !step.reversed : step.reversed;
        if (replaceSourceStart) {
            step.source.setStartPoint(point);
            return;
        }
        step.source.setEndPoint(point);
    }

    /**
     * 清理已经完全脱离草图边的旧端点。
     *
     * @param point 待清理旧端点。
     * @param canonicalPoint 最终共享点。
     */
    private removeDetachedPoint(point: SketchPointObject, canonicalPoint: SketchPointObject): void {
        if (point === canonicalPoint || point.getConnectedEdgesSnapshot().length > 0) {
            return;
        }
        if (point.parent) {
            point.parent.remove(point);
        }
    }
}
//------ 草图闭合端点统一 结束------
